/**
 * Shared by every caller of /api/payment/complete (currently the event and
 * course detail pages; bevent/[id] is Task 10). Extracted out of the pages
 * so a third caller has something to reuse instead of a third verbatim copy
 * – duplicated logic drifting apart between a page and its siblings is the
 * root cause behind several other findings in this audit.
 */
import { useEffect, useRef, type RefObject } from 'react';

export type PaymentCompleteClassification = 'success' | 'pending' | 'failure' | 'blocked';

/**
 * /api/payment/complete answers with more than success/failure: 202 `{ pending: true }`
 * means the gateway has no record of the payment yet (the customer is typically
 * still on the payment page), and 503 `{ pending: true }` means we couldn't reach
 * the gateway at all – our own outage. Both mean "not yet", not "no". Gating on
 * `res.ok && result.success` alone – as every caller used to – routes both into
 * the failure branch and tells someone their payment failed while it's in flight.
 *
 * A 409 with `errorCode: 'FULFILMENT_BLOCKED'` (route.ts:196-206) is a third,
 * distinct kind of "no": the gateway genuinely took the money, but the
 * enrollment could not be created (support is alerted server-side already –
 * see recordFulfilmentBlocked). Every other 409 still means 'failure' – this
 * only carves out the one whose body names this exact errorCode, so an
 * ordinary declined/invalid-signature 409 is unaffected.
 */
export function classifyPaymentCompleteResponse(
  res: { ok: boolean; status: number },
  result: { success?: boolean; pending?: boolean; errorCode?: string } | null | undefined
): PaymentCompleteClassification {
  if (res.ok && result?.success) return 'success';
  if ((res.status === 202 || res.status === 503) && result?.pending) return 'pending';
  if (res.status === 409 && result?.errorCode === 'FULFILMENT_BLOCKED') return 'blocked';
  return 'failure';
}

/**
 * Bounded poll of the existing enrollment-status check while a payment is
 * confirming server-side (webhook or the reconciliation cron will settle it).
 * Never polls forever, and stops immediately once `isCancelled()` reports the
 * caller has gone away (e.g. the component unmounted).
 */
export function schedulePendingPaymentPoll(options: {
  attempt?: number;
  maxAttempts?: number;
  intervalMs?: number;
  isCancelled: () => boolean;
  checkEnrollment: () => Promise<boolean>;
  onResolved: () => void;
  onExhausted: () => void;
  scheduleNext?: (run: () => void, ms: number) => void;
}): void {
  const {
    attempt = 0,
    maxAttempts = 12, // ~60s at the default 5s interval
    intervalMs = 5000,
    isCancelled,
    checkEnrollment,
    onResolved,
    onExhausted,
    scheduleNext = (run, ms) => { setTimeout(run, ms); },
  } = options;

  if (isCancelled()) return;

  checkEnrollment()
    .catch(() => false)
    .then((found) => {
      if (isCancelled()) return;
      if (found) {
        onResolved();
        return;
      }
      if (attempt + 1 >= maxAttempts) {
        onExhausted();
        return;
      }
      scheduleNext(() => {
        schedulePendingPaymentPoll({ ...options, attempt: attempt + 1 });
      }, intervalMs);
    });
}

/**
 * Dispatches the 'pending', 'failure' and 'blocked' side effects of a
 * classified /api/payment/complete response – the exact toast/state pair
 * that used to be wrong for 'pending' (an error toast telling the
 * participant their payment failed while it was still being confirmed).
 * 'blocked' gets its own message for the same reason: telling someone whose
 * card was just charged that they simply "failed to enroll" – 'failure''s
 * message – invites them to pay again and produce a second capture. 'success'
 * is left to the caller since its analytics payload differs per page.
 */
export function handlePaymentCompleteClassification(
  classification: 'pending' | 'failure' | 'blocked',
  deps: {
    toast: { success: (message: string) => void; error: (message: string) => void; info: (message: string) => void };
    t: (key: string) => string;
    setPaymentPending: (value: boolean) => void;
    startPendingPoll: () => void;
  }
): void {
  if (classification === 'pending') {
    deps.setPaymentPending(true);
    deps.toast.info(deps.t('courseDetails.paymentConfirming'));
    deps.startPendingPoll();
  } else if (classification === 'blocked') {
    deps.toast.error(deps.t('courseDetails.fulfilmentBlocked'));
  } else {
    deps.toast.error(deps.t('courseDetails.failedToEnroll'));
  }
}

/**
 * Cancellation flag for schedulePendingPaymentPoll's `isCancelled` callback.
 * Resets to `false` on every mount, not only when the ref is first created:
 * React Strict Mode's dev double-invoke runs this effect's cleanup (which
 * flips the ref to `true`) and then re-runs the effect on the SAME ref
 * instance. Without the reset the ref stays permanently `true` after the
 * first double-invoke, the poll bails out on its very first check,
 * `onExhausted` never fires, and the "confirming your payment" banner spins
 * forever. Sets the ref back to `true` on unmount so an in-flight poll
 * stops once the component is actually gone.
 *
 * Extracted as a hook (rather than the same `useRef` + `useEffect` pair
 * copied into event/[id], courses/[id] and bevent/[id]) so this specific
 * lifecycle bug has one implementation to test and to fix, not three.
 */
/**
 * The mount/cleanup effect body `usePollCancelledRef` installs, factored
 * out so its exact contract – reset to `false` on every run, flip to
 * `true` on cleanup – can be unit-tested directly against a plain ref-like
 * object, independent of whether a given test environment's React build
 * actually reproduces Strict Mode's dev-only effect double-invoke (jsdom
 * under jest here does not: NODE_ENV is 'test', not 'development', and the
 * double-invoke is gated on the development build).
 */
export function pollCancelledEffect(ref: { current: boolean }): () => void {
  ref.current = false;
  return () => { ref.current = true; };
}

export function usePollCancelledRef(): RefObject<boolean> {
  const ref = useRef(false);
  useEffect(() => pollCancelledEffect(ref), []);
  return ref;
}
